import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, AED_short, AED } from '../../lib/api';
import MobileShell from '../../components/MobileShell';
import {
  Heart, X, ExternalLink, Loader2, ChevronRight, ChevronLeft,
  ThumbsUp, ShieldAlert, TrendingUp, Sparkles, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

function timeLeft(iso) {
  if (!iso) return { text: '—', urgent: false, ended: false };
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { text: 'Ended', urgent: false, ended: true };
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d >= 1) return { text: `${d}d ${h}h`, urgent: d < 2, ended: false };
  if (h >= 1) return { text: `${h}h ${m}m`, urgent: true, ended: false };
  return { text: `${m}m`, urgent: true, ended: false };
}

/**
 * MobileAuctionDetail — dedicated page (was a fixed-inset modal overlay).
 * Route: /m/auctions/:externalId
 * User philosophy: no modals on mobile — everything routes to a full page.
 */
export default function MobileAuctionDetail() {
  const { externalId } = useParams();
  const nav = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/auctions/${externalId}`).then(r => {
      setDetail(r.data);
    }).catch(() => {
      toast.error('Auction not found');
    }).finally(() => setLoading(false));
  }, [externalId]);

  const toggleWatch = async () => {
    try {
      const r = await api.post(`/auctions/${externalId}/watch`);
      setDetail(d => ({ ...d, is_watched: r.data.watched }));
      toast.success(r.data.watched ? 'Watching' : 'Removed');
    } catch { toast.error('Failed'); }
  };

  const runAI = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/auctions/${externalId}/analyze`);
      setDetail(d => ({ ...d, ai_analysis: r.data.analysis }));
      toast.success('Iris AI ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI failed');
    } finally { setAnalyzing(false); }
  };

  const sendToDA = () => {
    if (!detail) return;
    const sizeSqft = typeof detail.size_sqft === 'number'
      ? detail.size_sqft
      : parseFloat(String(detail.area || '').replace(/[^\d.]/g, '')) || '';
    const q = new URLSearchParams({
      title: detail.title || '',
      community: detail.address || detail.emirate || '',
      building: detail.title || detail.subtitle || '',
      size_sqft: sizeSqft + '',
      bedrooms: detail.bedrooms || 0,
      bathrooms: detail.bathrooms || 0,
      asking_price_aed: detail.current_bid_aed || detail.starting_bid_aed || '',
      listing_url: detail.listing_url || '',
      notes: `From Emirates Auction #${detail.external_id}`,
    });
    nav(`/m/deal-analyzer?${q.toString()}`);
  };

  if (loading) {
    return (
      <MobileShell noHeader>
        <div className="pt-6 flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-slate-400" size={22}/>
          <div className="text-xs text-slate-500">Loading auction…</div>
        </div>
      </MobileShell>
    );
  }
  if (!detail) {
    return (
      <MobileShell noHeader>
        <div className="pt-8 text-center">
          <div className="text-sm text-slate-500 mb-3">Auction not found.</div>
          <Link to="/m/auctions" className="text-[#679286] text-sm font-medium">← Back to auctions</Link>
        </div>
      </MobileShell>
    );
  }

  const tl = timeLeft(detail.auction_end_at);
  const ai = detail.ai_analysis;
  const imgs = detail.images || [];

  return (
    <MobileShell noHeader>
      {/* Header row */}
      <div className="pt-3 pb-2 flex items-center gap-2" data-testid="m-auction-detail-page">
        <button
          onClick={() => nav(-1)}
          className="w-10 h-10 rounded-full bg-white/85 border border-white/70 grid place-items-center shadow-sm"
          data-testid="m-auction-back"
        >
          <ArrowLeft size={16}/>
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">#{detail.external_id}</div>
          <div className="font-serif text-lg truncate leading-tight">{detail.title || 'Auction listing'}</div>
        </div>
        <button
          onClick={toggleWatch}
          className="w-10 h-10 rounded-full bg-white/85 border border-white/70 grid place-items-center shadow-sm"
          data-testid="m-auction-watch"
        >
          <Heart size={16} className={detail.is_watched ? 'fill-red-500 text-red-500' : 'text-slate-500'}/>
        </button>
      </div>

      <div className="space-y-4 pb-24">
        {imgs.length > 0 && (
          <div>
            <div
              className="relative rounded-2xl overflow-hidden bg-slate-200 h-56"
              onClick={() => setLightbox(true)}
              data-testid="m-auction-hero"
            >
              <img src={imgs[heroIdx]} alt="" className="w-full h-full object-cover"/>
              {imgs.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setHeroIdx(i => (i - 1 + imgs.length) % imgs.length); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 grid place-items-center shadow"
                    aria-label="Prev"
                  ><ChevronLeft size={16}/></button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setHeroIdx(i => (i + 1) % imgs.length); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 grid place-items-center shadow"
                    aria-label="Next"
                  ><ChevronRight size={16}/></button>
                  <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-semibold">
                    {heroIdx + 1}/{imgs.length}
                  </div>
                </>
              )}
            </div>
            {imgs.length > 1 && (
              <div className="mt-2 flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 no-scrollbar">
                {imgs.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setHeroIdx(i)}
                    data-testid={`m-thumb-${i}`}
                    className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-slate-200 ring-2 ${
                      i === heroIdx ? 'ring-[#679286]' : 'ring-transparent'
                    }`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" loading="lazy"/>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Stats — 3 tiles */}
        <div className="grid grid-cols-3 gap-2">
          <StatTile label="Bid" value={AED_short(detail.current_bid_aed)} sub={`${detail.bid_count || 0} bids`} accent="text-[#679286]"/>
          <StatTile label="Time left" value={tl.text} accent={tl.urgent ? 'text-red-500' : 'text-slate-900'}/>
          <StatTile label="Type" value={detail.property_type_code || '—'} sub={detail.is_physical ? 'Physical' : 'Online'}/>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={runAI} disabled={analyzing}
            data-testid="m-auction-run-ai"
            className="flex items-center justify-center gap-2 px-3 py-3 rounded-2xl bg-[#1A2530] text-white text-sm font-semibold disabled:opacity-60 active:scale-[0.98] transition-transform"
          >
            {analyzing ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
            {ai ? 'Re-analyze' : 'Ask Iris AI'}
          </button>
          <button
            onClick={sendToDA}
            data-testid="m-auction-send-to-da"
            className="flex items-center justify-center gap-2 px-3 py-3 rounded-2xl bg-white/85 border border-white/70 text-sm font-semibold active:scale-[0.98] transition-transform"
          >
            <TrendingUp size={14}/>
            Deal Analyzer
          </button>
        </div>

        {detail.listing_url && (
          <a
            href={detail.listing_url} target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-white/70 border border-white/60 text-xs font-medium text-slate-600"
            data-testid="m-auction-external"
          >
            <ExternalLink size={12}/> View on Emirates Auction
          </a>
        )}

        {ai && (
          <div className="glass p-4 border-l-4 border-[#679286]" data-testid="m-auction-ai-insight">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-[#679286]"/>
              <div className="font-serif text-base">Iris AI verdict</div>
              <div className="ml-auto text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-900/85 text-white">
                {(ai.recommendation || 'watch').replace('_', ' ')}
              </div>
            </div>
            <div className="text-sm text-slate-800 mb-3">{ai.verdict || ai.summary}</div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {ai.deal_score != null && <div className="rounded-lg bg-white/70 p-2 text-center"><div className="text-slate-500 text-[10px]">Score</div><div className="font-semibold text-[#679286]">{Math.round(ai.deal_score)}</div></div>}
              {ai.projected_roi_pct != null && <div className="rounded-lg bg-white/70 p-2 text-center"><div className="text-slate-500 text-[10px]">ROI</div><div className="font-semibold text-emerald-600">{ai.projected_roi_pct}%</div></div>}
              {ai.max_bid_aed && <div className="rounded-lg bg-white/70 p-2 text-center"><div className="text-slate-500 text-[10px]">Max bid</div><div className="font-semibold">{AED_short(ai.max_bid_aed)}</div></div>}
            </div>
            {ai.positives?.length ? (
              <div className="mt-3">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-600 mb-1"><ThumbsUp size={10}/> Positives</div>
                <ul className="space-y-0.5 text-xs text-slate-700 list-disc pl-4">{ai.positives.map((p,i)=><li key={i}>{p}</li>)}</ul>
              </div>
            ) : null}
            {ai.risks?.length ? (
              <div className="mt-3">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-red-500 mb-1"><ShieldAlert size={10}/> Risks</div>
                <ul className="space-y-0.5 text-xs text-slate-700 list-disc pl-4">{ai.risks.map((p,i)=><li key={i}>{p}</li>)}</ul>
              </div>
            ) : null}
          </div>
        )}

        <div className="glass p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Listing details</div>
          <div className="space-y-1.5 text-sm">
            <MRow k="Emirate" v={detail.emirate}/>
            <MRow k="Address" v={detail.address}/>
            <MRow k="Area" v={detail.area}/>
            <MRow k="Type" v={detail.property_type_code}/>
            <MRow k="Starting bid" v={AED(detail.starting_bid_aed)}/>
            <MRow k="End" v={detail.auction_end_at?.slice(0, 16).replace('T', ' ') + ' UTC'}/>
          </div>
          {detail.description && (
            <div className="mt-3 pt-3 border-t border-white/60 text-xs text-slate-700 whitespace-pre-wrap max-h-52 overflow-y-auto">
              {detail.description}
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen lightbox */}
      {lightbox && imgs.length > 0 && (
        <div
          onClick={() => setLightbox(false)}
          className="fixed inset-0 z-[60] bg-black/95 grid place-items-center"
          data-testid="m-auction-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 text-white grid place-items-center"
            aria-label="Close"
          ><X size={20}/></button>
          {imgs.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setHeroIdx(i => (i - 1 + imgs.length) % imgs.length); }}
                className="absolute left-3 w-11 h-11 rounded-full bg-white/10 text-white grid place-items-center"
              ><ChevronLeft size={20}/></button>
              <button
                onClick={(e) => { e.stopPropagation(); setHeroIdx(i => (i + 1) % imgs.length); }}
                className="absolute right-3 w-11 h-11 rounded-full bg-white/10 text-white grid place-items-center"
              ><ChevronRight size={20}/></button>
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-xs">
                {heroIdx + 1} / {imgs.length}
              </div>
            </>
          )}
          <img
            src={imgs[heroIdx]} alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-[94vw] max-h-[84vh] object-contain"
          />
        </div>
      )}
    </MobileShell>
  );
}

function StatTile({ label, value, sub, accent = 'text-slate-900' }) {
  return (
    <div className="glass p-3 text-center">
      <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-serif text-lg ${accent}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

function MRow({ k, v }) {
  return <div className="flex justify-between gap-2"><span className="text-slate-500">{k}</span><span className="font-medium text-right truncate">{v ?? '—'}</span></div>;
}
